/** @jsxImportSource @emotion/react */
import { Box, Button, Heading, Text } from '@landtrustinc/design-system';
import { Elements, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import React, { useMemo, useState } from 'react';

import type { BuckChatConfig, LtPlusBillingInterval, StripeConfig } from '../../../types';
import type { AnalyticsHandler } from '../../SignupDrawer/hooks/useSignupFlow';
import { useCheckoutStep } from '../hooks/useCheckoutStep';
import { formatLtPlusCurrency, getLtPlusPlanAmount } from '../ltPlusPlans';
import * as checkoutStyles from '../styles';
import { PaymentForm } from '../styles';

const stripeAppearance = {
  variables: {
    fontFamily: `'Open Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', sans-serif`,
    colorText: '#1a202c',
    colorTextPlaceholder: '#718096',
    borderRadius: '5px',
    colorDanger: '#ca2d10',
  },
  rules: {
    '.Label': {
      fontFamily: `'Open Sans', system-ui, sans-serif`,
      fontSize: '16px',
      fontWeight: 'bold',
      marginBottom: '6px',
    },
    '.Input': {
      border: '2px solid #cbd5e0',
      fontFamily: `'Open Sans', system-ui, sans-serif`,
    },
    '.Tab': {
      borderWidth: '2px',
    },
  },
};

type CheckoutStepProps = {
  stripeConfig: StripeConfig;
  buckChatConfig: BuckChatConfig;
  billingInterval: LtPlusBillingInterval;
  onSuccess: () => void;
  onBack: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

const CheckoutForm: React.FC<CheckoutStepProps> = ({
  stripeConfig,
  buckChatConfig,
  billingInterval,
  onSuccess,
  onBack,
  onAnalyticsEvent,
}) => {
  const { handleSubmit, isSubmitting, error, handleBack, displayPrice } = useCheckoutStep({
    stripeConfig,
    buckChatConfig,
    billingInterval,
    onSuccess,
    onBack,
    onAnalyticsEvent,
  });
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

  return (
    <Box display="flex" flexDirection="column" gap="var(--spacing-4)">
      <Box display="flex" flexDirection="column" gap="var(--spacing-1)">
        <Heading size="xs" fontWeight="bold">
          Pay with
        </Heading>
        <Text size="sm">All transactions are secure and encrypted.</Text>
      </Box>

      {error && <Box css={checkoutStyles.errorMessageStyles}>{error}</Box>}

      <PaymentForm onSubmit={handleSubmit}>
        {!isPaymentElementReady && <Text size="sm">Loading...</Text>}
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
          }}
          onReady={() => setIsPaymentElementReady(true)}
        />

        {isPaymentElementReady && (
          <>
            <Button
              variant="plus"
              fullWidth
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Get LandTrust+
            </Button>

            <Button variant="secondary" fullWidth onClick={handleBack}>
              Back
            </Button>

            <Box
              display="flex"
              flexDirection="column"
              gap="var(--spacing-1)"
              textAlign="center"
              mt="var(--spacing-2)"
            >
              <Text size="sm" fontWeight="bold">
                {billingInterval === 'annual' ? 'Less than $1 per day' : 'Flexible monthly billing'}
              </Text>
              <Text size="sm" color="var(--color-gray-500)">
                {billingInterval === 'annual'
                  ? `Billed yearly at ${formatLtPlusCurrency(displayPrice * 100)}`
                  : `Billed monthly at ${formatLtPlusCurrency(displayPrice * 100)}`}
              </Text>
            </Box>
          </>
        )}
      </PaymentForm>
    </Box>
  );
};

const CheckoutStep: React.FC<CheckoutStepProps> = ({
  stripeConfig,
  buckChatConfig,
  billingInterval,
  onSuccess,
  onBack,
  onAnalyticsEvent,
}) => {
  const stripePromise = useMemo(
    () => loadStripe(stripeConfig.publishableKey),
    [stripeConfig.publishableKey]
  );

  const options: StripeElementsOptions = useMemo(
    () => ({
      mode: 'subscription',
      amount: getLtPlusPlanAmount(stripeConfig, billingInterval),
      currency: 'usd',
      appearance: { theme: 'stripe', ...stripeAppearance },
    }),
    [billingInterval, stripeConfig]
  );

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm
        stripeConfig={stripeConfig}
        buckChatConfig={buckChatConfig}
        billingInterval={billingInterval}
        onSuccess={onSuccess}
        onBack={onBack}
        onAnalyticsEvent={onAnalyticsEvent}
      />
    </Elements>
  );
};

export default CheckoutStep;
